import { useMutation, useQuery, UseQueryOptions } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { MetaApiResponse } from 'utils/types/CommonApiResponse';
import { createQaChunk, deleteQaChunk, getQaChunk, getQaChunks, importQaChunk, updateQaChunk } from './api';
import { queryClient } from 'providers/AllProviders';
import { QaChunkRequest, QaChunkResponse } from './types';

export function useGetQaChunks(
  params?: Record<string, any>,
  options?: UseQueryOptions<{ data: QaChunkResponse[]; meta: MetaApiResponse }, Error>
) {
  const { locationId, agentId } = useParams();

  return useQuery<{ data: QaChunkResponse[]; meta: MetaApiResponse }>({
    queryKey: ['qaChunks', locationId, agentId, params],
    queryFn: () => getQaChunks(locationId!, agentId!, params),
    enabled: Boolean(agentId && locationId),
    ...options
  });
}

export function useGetQaChunk(options?: UseQueryOptions<QaChunkResponse, Error>) {
  const { locationId, agentId, chunkId } = useParams();

  return useQuery<QaChunkResponse>({
    queryKey: ['qaChunk', locationId, agentId, chunkId],
    queryFn: () => getQaChunk(locationId!, agentId!, chunkId!),
    enabled: Boolean(agentId && locationId && chunkId),
    ...options
  });
}

export function useCreateOrUpdateQaChunk() {
  const navigate = useNavigate();
  const { locationId, agentId, chunkId } = useParams<{
    locationId: string;
    agentId: string;
    chunkId: string;
  }>();

  return useMutation({
    mutationFn: (payload: QaChunkRequest) =>
      chunkId ? updateQaChunk(locationId!, agentId!, chunkId!, payload) : createQaChunk(locationId!, agentId!, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['qaChunks', locationId, agentId],
        refetchType: 'active'
      });
      queryClient.invalidateQueries({
        queryKey: ['agent', locationId, agentId],
        refetchType: 'active'
      });
      if (chunkId) {
        queryClient.invalidateQueries({
          queryKey: ['qaChunks', locationId, agentId, chunkId],
          refetchType: 'active'
        });
      }
      navigate(-1);
    }
  });
}

export function useDeleteQaChunk() {
  const { locationId, agentId } = useParams<{ locationId: string; agentId: string }>();

  return useMutation({
    mutationFn: (id: string) => deleteQaChunk(locationId!, agentId!, id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['qaChunks'],
        refetchType: 'active'
      });
      queryClient.invalidateQueries({
        queryKey: ['agent', locationId, agentId],
        refetchType: 'active'
      });
    }
  });
}

export function useImportQaChunk() {
  const { locationId, agentId } = useParams<{ locationId: string; agentId: string }>();

  return useMutation({
    mutationFn: (payload: FormData) => importQaChunk(locationId!, agentId!, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['s'],
        refetchType: 'active'
      });
    }
  });
}
